/**
 * CRMCalendar — Vague 113 Livraison 1
 *
 * Full calendar experience embedded inside /app/crm (tab "Calendrier").
 *
 * Views: month / week / day / agenda (list).
 * Events are fetched on view/date change with start/end ISO bounds.
 * Click an empty slot → create. Click an existing event → edit.
 * Each event dot uses the color of its type_key.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, X, Loader2, Check,
  Clock, MapPin, Building2, Palette, Trash2, Bell, CheckCircle2, Phone, Users,
  CheckSquare, Mail, FileText, Tag, Settings2, Share2,
} from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';
import { useCalendarSettings } from '../../hooks/useCalendarSettings';
import CalendarSettingsDialog from '../../components/CalendarSettingsDialog';
import CalendarShareDialog from '../../components/CalendarShareDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';

const ICONS = { Phone, Users, CheckSquare, Mail, FileText, Tag, Calendar: CalendarIcon };

function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; }
function endOfMonth(d) { const x = new Date(d); x.setMonth(x.getMonth()+1, 0); x.setHours(23,59,59,999); return x; }
function startOfWeek(d, firstDow = 0) { const x = new Date(d); const day = (x.getDay() + 7 - firstDow) % 7; x.setDate(x.getDate() - day); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function isSameDay(a, b) { return a.getDate()===b.getDate() && a.getMonth()===b.getMonth() && a.getFullYear()===b.getFullYear(); }
function fmtMonth(d) { return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }); }
function fmtDay(d) { return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); }
function toIsoLocal(d) { const pad=(n)=>String(n).padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; }
// Split / combine helpers for the Date + Time-select inputs. Using
// datetime-local would let the browser's native picker show a minute-by-
// minute column which contradicts the 15-min slot granularity.
function splitLocal(iso) {
  if (!iso) return { date: '', time: '' };
  // iso is a "YYYY-MM-DDTHH:mm" local string (from toIsoLocal) or an ISO.
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (m) return { date: m[1], time: m[2] };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: '', time: '' };
  return { date: toIsoLocal(d).slice(0, 10), time: toIsoLocal(d).slice(11, 16) };
}
function combineLocal(date, time) {
  if (!date) return '';
  return `${date}T${(time || '00:00').slice(0, 5)}`;
}
// Generate time-of-day options every `slot` minutes, e.g. ["00:00","00:15",...]
function timeSlotOptions(slot) {
  const s = Math.max(5, Math.min(60, slot || 15));
  const pad = (n) => String(n).padStart(2, '0');
  const out = [];
  for (let total = 0; total < 24 * 60; total += s) {
    out.push(`${pad(Math.floor(total / 60))}:${pad(total % 60)}`);
  }
  return out;
}
// Consistent *local* YYYY-MM-DD key. Using toISOString().slice(0,10) would
// shift events by one day in UTC+N or UTC-N timezones (local midnight is the
// previous/next day in UTC). That caused the drag-and-drop bug where an
// event dropped on Tuesday would actually land on Monday.
function localDateKey(d) { const pad=(n)=>String(n).padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }

export default function CRMCalendar() {
  const { settings, update: updateSettings, reset: resetSettings, ready: settingsReady } = useCalendarSettings();
  const [view, setView] = useState(settings.default_view || 'month');
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // event being edited or { start_at } pre-filled for create
  const [typesOpen, setTypesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [me, setMe] = useState(null);            // current user_id for owner comparison
  const [incomingShares, setIncomingShares] = useState([]); // [{owner:{user_id,name,picture,...}}]

  // Sync default view from settings once they finish hydrating.
  useEffect(() => {
    if (settingsReady && settings.default_view && settings.default_view !== view) {
      setView(settings.default_view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsReady]);

  // Date window based on view
  const { start, end, title } = useMemo(() => {
    const firstDow = settings.first_day_of_week ?? 0;
    const s = startOfMonth(cursor);
    let e = endOfMonth(cursor); let t = fmtMonth(cursor);
    if (view === 'week' || view === 'workweek') {
      const wk = startOfWeek(cursor, firstDow);
      const days = view === 'workweek' ? 5 : 7;
      t = `${view === 'workweek' ? 'Semaine travail' : 'Semaine'} du ${wk.toLocaleDateString('fr-FR',{day:'numeric',month:'long'})}`;
      return { start: wk, end: addDays(wk, days), title: t };
    }
    if (view === 'day') {
      const d0 = new Date(cursor); d0.setHours(0,0,0,0);
      return { start: d0, end: addDays(d0, 1), title: fmtDay(cursor) };
    }
    if (view === 'agenda') {
      const d0 = new Date(); d0.setHours(0,0,0,0);
      return { start: d0, end: addDays(d0, 30), title: 'Agenda — 30 prochains jours' };
    }
    return { start: s, end: addDays(e, 1), title: t };
  }, [view, cursor, settings.first_day_of_week]);

  // Visible events after applying "hide done / cancelled" prefs
  const visibleEvents = useMemo(() => {
    return events.filter((e) => {
      if (settings.hide_done && e.status === 'done') return false;
      if (settings.hide_cancelled && e.status === 'cancelled') return false;
      return true;
    });
  }, [events, settings.hide_done, settings.hide_cancelled]);

  const reload = async () => {
    setLoading(true);
    try {
      // Build the list of agendas to query: self + teammates whose agendas
      // the user has toggled "visible". Backend validates each owner_id
      // against the calendar_shares collection so the client can't sneak
      // in a non-authorized user_id.
      const visible = Array.from(new Set([
        ...(me ? [me] : []),
        ...(settings.visible_owner_ids || []),
      ]));
      const params = { start: start.toISOString(), end: end.toISOString() };
      if (visible.length) params.owner_ids = visible.join(',');
      const [ev, tp] = await Promise.all([
        api.get('/calendar/events', { params }),
        api.get('/calendar/event-types'),
      ]);
      setEvents(ev.data || []);
      setTypes(tp.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [start, end, me, settings.visible_owner_ids]);

  // Load CRM lookups + identity + incoming shares on mount.
  useEffect(() => {
    api.get('/companies').then(({ data }) => setCompanies(data || [])).catch(() => {});
    api.get('/prospects').then(({ data }) => setProspects(data || [])).catch(() => {});
    api.get('/auth/me').then(({ data }) => setMe(data?.user_id)).catch(() => {});
    api.get('/calendar/shares').then(({ data }) => setIncomingShares(data?.incoming || [])).catch(() => {});
  }, []);

  // Quick lookup: owner user_id → snapshot (name, picture) for rendering.
  const ownersById = useMemo(() => {
    const map = {};
    for (const s of incomingShares) {
      if (s.owner?.user_id) map[s.owner.user_id] = s.owner;
    }
    return map;
  }, [incomingShares]);

  // Refresh incoming shares when the share dialog closes so the visibility
  // pills stay in sync with any revoke-from-there action.
  const refreshShares = () =>
    api.get('/calendar/shares').then(({ data }) => setIncomingShares(data?.incoming || [])).catch(() => {});

  const typeByKey = useMemo(() => Object.fromEntries(types.map((t) => [t.key, t])), [types]);

  const shift = (dir) => {
    const d = new Date(cursor);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week' || view === 'workweek') d.setDate(d.getDate() + 7*dir);
    else if (view === 'day') d.setDate(d.getDate() + dir);
    else d.setDate(d.getDate() + 30*dir);
    setCursor(d);
  };

  const onSlotClick = (date) => {
    const startAt = new Date(date);
    if (startAt.getHours() === 0 && startAt.getMinutes() === 0) startAt.setHours(9, 0, 0, 0);
    const duration = settings.default_duration_minutes || 30;
    const endAt = new Date(startAt.getTime() + duration * 60 * 1000);
    setEditing({ isNew: true, start_at: startAt.toISOString(), end_at: endAt.toISOString(), type_key: 'rdv', reminders: settings.default_reminders || [15] });
  };

  // Drag-and-drop reschedule — preserve duration, just shift start.
  const moveEvent = async (eventId, newStart) => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    const duration = new Date(ev.end_at) - new Date(ev.start_at);
    const newEnd = new Date(newStart.getTime() + duration);
    const payload = {
      title: ev.title, description: ev.description, type_key: ev.type_key,
      start_at: newStart.toISOString(), end_at: newEnd.toISOString(),
      all_day: ev.all_day, company_id: ev.company_id, prospect_id: ev.prospect_id,
      contact_id: ev.contact_id, location: ev.location, reminders: ev.reminders,
      status: ev.status,
    };
    // Optimistic update — UX feels instant even if the PUT takes a moment.
    setEvents((arr) => arr.map((e) => e.id === eventId ? { ...e, start_at: newStart.toISOString(), end_at: newEnd.toISOString() } : e));
    try {
      await api.put(`/calendar/events/${eventId}`, payload);
      toast({ title: 'Événement déplacé' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec du déplacement' });
      reload();
    }
  };

  return (
    <div className="space-y-4" data-testid="crm-calendar">
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-2">
        <button onClick={() => setCursor(new Date())} data-testid="cal-today"
                className="h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] font-semibold hover:bg-[#FAFAFE]">Aujourd'hui</button>
        <div className="flex items-center bg-white border border-[#EFF0F6] rounded-lg">
          <button onClick={() => shift(-1)} className="h-9 w-9 flex items-center justify-center hover:bg-[#FAFAFE] rounded-l-lg"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => shift(1)} className="h-9 w-9 flex items-center justify-center hover:bg-[#FAFAFE] rounded-r-lg"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="text-[14px] font-semibold text-[#1A1B2E] capitalize px-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="cal-title">{title}</div>
        <div className="ml-auto flex items-center gap-1 bg-white border border-[#EFF0F6] rounded-lg p-1" data-testid="cal-view-switch">
          {[['month','Mois'],['workweek','Sem. 5j'],['week','Sem. 7j'],['day','Jour'],['agenda','Liste']].map(([v, lbl]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                    className={`h-7 px-3 rounded-md text-[12px] font-semibold ${view===v ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-[#FAFAFE]'}`}>{lbl}</button>
          ))}
        </div>
        <button onClick={() => setTypesOpen(true)} data-testid="cal-types-btn"
                className="h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FAFAFE]">
          <Palette className="w-3.5 h-3.5" /> Types
        </button>
        <button onClick={() => setShareOpen(true)} data-testid="cal-share-btn"
                title="Partage d'agenda"
                className="h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FAFAFE]">
          <Share2 className="w-3.5 h-3.5" /> Partage
          {(settings.visible_owner_ids || []).length > 0 && (
            <span className="ml-1 bg-[#4F46E5] text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">
              {(settings.visible_owner_ids || []).length}
            </span>
          )}
        </button>
        <button onClick={() => setSettingsOpen(true)} data-testid="cal-settings-btn"
                title="Paramètres du calendrier"
                className="h-9 w-9 rounded-lg bg-white border border-[#EFF0F6] flex items-center justify-center hover:bg-[#FAFAFE]">
          <Settings2 className="w-4 h-4 text-[#6B6C85]" />
        </button>
        <button onClick={() => onSlotClick(new Date())} data-testid="cal-create-btn"
                className="h-9 px-3.5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Nouvel événement
        </button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 flex-wrap">
        {types.map((t) => (
          <span key={t.key} className="inline-flex items-center gap-1.5 text-[11.5px] px-2 py-1 rounded-md border border-[#EFF0F6] bg-white">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
            {t.label}
          </span>
        ))}
      </div>

      {/* Body */}
      {loading ? (
        <div className="soft-card p-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" /></div>
      ) : view === 'month' ? (
        <MonthGrid cursor={cursor} events={visibleEvents} typeByKey={typeByKey} onSlotClick={onSlotClick} onEventClick={setEditing} onMoveEvent={moveEvent} firstDow={settings.first_day_of_week ?? 0} showWeekNumbers={settings.show_week_numbers} me={me} ownersById={ownersById} />
      ) : view === 'week' || view === 'workweek' ? (
        <WeekGrid start={start} days={view === 'workweek' ? 5 : 7} events={visibleEvents} typeByKey={typeByKey} onSlotClick={onSlotClick} onEventClick={setEditing} onMoveEvent={moveEvent} settings={settings} me={me} ownersById={ownersById} />
      ) : view === 'day' ? (
        <DayGrid date={cursor} events={visibleEvents} typeByKey={typeByKey} onSlotClick={onSlotClick} onEventClick={setEditing} onMoveEvent={moveEvent} settings={settings} me={me} ownersById={ownersById} />
      ) : (
        <AgendaList events={visibleEvents} typeByKey={typeByKey} companies={companies} prospects={prospects} onEventClick={setEditing} me={me} ownersById={ownersById} />
      )}

      {editing && (
        <EventDialog
          event={editing} types={types} companies={companies} prospects={prospects}
          settings={settings}
          me={me} ownersById={ownersById}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {typesOpen && (
        <TypesDialog types={types} onClose={() => setTypesOpen(false)} onChange={reload} />
      )}
      {settingsOpen && (
        <CalendarSettingsDialog
          open={settingsOpen}
          settings={settings}
          onChange={updateSettings}
          onReset={resetSettings}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {shareOpen && (
        <CalendarShareDialog
          open={shareOpen}
          visibleOwnerIds={settings.visible_owner_ids || []}
          onVisibleChange={(ids) => updateSettings({ visible_owner_ids: ids })}
          onClose={() => { setShareOpen(false); refreshShares(); }}
        />
      )}
    </div>
  );
}

/* =================== Month view =================== */
function MonthGrid({ cursor, events, typeByKey, onSlotClick, onEventClick, onMoveEvent, firstDow = 0, showWeekNumbers = false, me, ownersById = {} }) {
  const first = startOfMonth(cursor);
  const firstDay = startOfWeek(first, firstDow);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(firstDay, i));
  const [dragOver, setDragOver] = useState(null);
  const byDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const d = new Date(e.start_at); const key = localDateKey(d);
      (map[key] = map[key] || []).push(e);
    }
    return map;
  }, [events]);
  // Rotate weekday headers according to firstDow
  const allDows = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
  const headers = [...allDows.slice(firstDow), ...allDows.slice(0, firstDow)];

  const onDragStart = (e, ev) => {
    e.dataTransfer.setData('text/plain', ev.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDrop = (e, day) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData('text/plain');
    const src = events.find((x) => x.id === id); if (!src) return;
    const orig = new Date(src.start_at);
    const newStart = new Date(day);
    newStart.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
    onMoveEvent?.(id, newStart);
  };

  const isoWeek = (d) => {
    const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
    const yr = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    return Math.ceil((((t - yr) / 86400000) + 1) / 7);
  };

  return (
    <div className="soft-card overflow-hidden" data-testid="cal-month-grid">
      <div className={`grid border-b border-[#EFF0F6] ${showWeekNumbers ? 'grid-cols-[36px_repeat(7,1fr)]' : 'grid-cols-7'}`}>
        {showWeekNumbers && <div className="text-[10px] text-[#8E8FA5] text-center py-2">Sem.</div>}
        {headers.map((d) => (
          <div key={d} className="text-center text-[11.5px] font-semibold text-[#8E8FA5] py-2 uppercase tracking-wide">{d}</div>
        ))}
      </div>
      <div className={`grid ${showWeekNumbers ? 'grid-cols-[36px_repeat(7,1fr)]' : 'grid-cols-7'}`} style={{ gridAutoRows: 'minmax(110px, 1fr)' }}>
        {Array.from({ length: 6 }, (_, rowIdx) => {
          const weekCells = cells.slice(rowIdx * 7, rowIdx * 7 + 7);
          const weekStart = weekCells[0];
          return (
            <React.Fragment key={rowIdx}>
              {showWeekNumbers && (
                <div className="border-r border-b border-[#F3F4F8] flex items-start justify-center pt-2 text-[10.5px] font-bold text-[#8E8FA5]">
                  {isoWeek(weekStart)}
                </div>
              )}
              {weekCells.map((d, idx) => {
                const inMonth = d.getMonth() === cursor.getMonth();
                const today = isSameDay(d, new Date());
                const key = localDateKey(d);
                const dayEvents = byDay[key] || [];
                const isDragTarget = dragOver === key;
                return (
                  <div key={idx}
                       onClick={() => onSlotClick(d)}
                       onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
                       onDragLeave={() => setDragOver((cur) => cur === key ? null : cur)}
                       onDrop={(e) => onDrop(e, d)}
                       className={`border-r border-b border-[#F3F4F8] p-1.5 text-left cursor-pointer transition ${inMonth ? 'bg-white hover:bg-[#FAFAFE]' : 'bg-[#FAFAFE]/50 text-[#C7C8D6]'} ${isDragTarget ? 'ring-2 ring-inset ring-[#4F46E5] bg-[#EEF2FF]' : ''}`}>
                    <div className={`text-[11px] font-bold ${today ? 'text-white bg-[#4F46E5] w-6 h-6 rounded-full flex items-center justify-center' : 'text-[#6B6C85]'}`}>
                      {d.getDate()}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {dayEvents.slice(0, 3).map((e) => {
                        const t = typeByKey[e.type_key] || { color: '#8E8FA5' };
                        const isMine = !me || !e.created_by || e.created_by === me;
                        const owner = !isMine ? ownersById[e.created_by] : null;
                        return (
                          <button key={e.id} onClick={(ev) => { ev.stopPropagation(); onEventClick(e); }}
                                  draggable={isMine}
                                  onDragStart={(ev) => isMine && onDragStart(ev, e)}
                                  data-testid={`cal-event-${e.id}`}
                                  title={!isMine && owner ? `Agenda de ${owner.name}` : undefined}
                                  className={`w-full text-left text-[10.5px] font-semibold px-1.5 py-0.5 rounded truncate flex items-center gap-1 ${isMine ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${e.status === 'done' ? 'line-through opacity-60' : ''} ${!isMine ? 'ring-1 ring-inset ring-[#C7C8D6]' : ''}`}
                                  style={{ background: `${t.color}1F`, color: t.color }}>
                            {owner && (
                              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/70 text-[8px] font-bold flex-shrink-0"
                                    style={{ color: t.color }}>
                                {(owner.name || '?').charAt(0).toUpperCase()}
                              </span>
                            )}
                            <span className="truncate">
                              {e.all_day ? '' : new Date(e.start_at).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}) + ' · '}{e.title}
                            </span>
                          </button>
                        );
                      })}
                      {dayEvents.length > 3 && <div className="text-[10px] text-[#6B6C85] pl-1">+{dayEvents.length - 3}</div>}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/* =================== Time grid (week / workweek / day) =================== */
// Fully driven by user settings (start_hour, end_hour, slot_minutes).
function TimeGrid({ days, events, typeByKey, onSlotClick, onEventClick, onMoveEvent, settings, me, ownersById = {} }) {
  const [dragOverKey, setDragOverKey] = useState(null);
  const START_HOUR = settings?.start_hour ?? 7;
  const END_HOUR = settings?.end_hour ?? 21;
  const SLOT_MINUTES = settings?.slot_minutes ?? 15;
  const SLOTS_PER_HOUR = Math.round(60 / SLOT_MINUTES);
  const HOUR_PX = SLOT_MINUTES === 30 ? 56 : 64;
  const SLOT_PX = HOUR_PX / SLOTS_PER_HOUR;
  const HOUR_COUNT = Math.max(1, END_HOUR - START_HOUR);
  const COL_HEIGHT = HOUR_COUNT * HOUR_PX;

  const onDragStart = (e, ev) => {
    e.dataTransfer.setData('text/plain', ev.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDropSlot = (e, day, slotIndex) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const newStart = new Date(day);
    const totalMinutes = START_HOUR * 60 + slotIndex * SLOT_MINUTES;
    newStart.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
    onMoveEvent?.(id, newStart);
  };

  const byDayKey = useMemo(() => {
    const m = {};
    for (const e of events) {
      const k = localDateKey(new Date(e.start_at));
      (m[k] = m[k] || []).push(e);
    }
    return m;
  }, [events]);

  return (
    <div className="soft-card overflow-hidden" data-testid="cal-time-grid">
      {/* Header row */}
      <div className="grid border-b border-[#EFF0F6]" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
        <div />
        {days.map((d) => {
          const today = isSameDay(d, new Date());
          return (
            <div key={d.toISOString()} className={`text-center py-2 ${today ? 'bg-[#EEF2FF]' : ''}`}>
              <div className="text-[11px] uppercase text-[#8E8FA5] font-semibold">{d.toLocaleDateString('fr-FR',{weekday:'short'})}</div>
              <div className="text-[14px] font-bold text-[#1A1B2E]">{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      {/* Body */}
      <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)`, height: COL_HEIGHT }}>
        {/* Hour labels column — one label per HOUR only */}
        <div className="relative">
          {Array.from({ length: HOUR_COUNT }, (_, i) => (
            <div key={i} className="text-[10.5px] text-[#8E8FA5] pr-1 text-right absolute right-0" style={{ top: i * HOUR_PX - 6 }}>
              {String(START_HOUR + i).padStart(2,'0')}:00
            </div>
          ))}
        </div>
        {/* Day columns */}
        {days.map((d) => {
          const k = localDateKey(d);
          const dayEvents = (byDayKey[k] || []).filter((e) => {
            const h = new Date(e.start_at).getHours();
            return h >= START_HOUR && h < END_HOUR;
          });
          return (
            <div key={k} className="relative border-l border-[#F3F4F8]">
              {/* Hour lines (strong) + quarter lines (dashed light) */}
              {Array.from({ length: HOUR_COUNT + 1 }, (_, i) => (
                <div key={`h${i}`} className="absolute left-0 right-0 border-t border-[#EFF0F6]" style={{ top: i * HOUR_PX }} />
              ))}
              {Array.from({ length: HOUR_COUNT * (SLOTS_PER_HOUR - 1) }, (_, i) => {
                const slotIdx = i + Math.floor(i / (SLOTS_PER_HOUR - 1));
                return <div key={`q${i}`} className="absolute left-0 right-0 border-t border-dashed border-[#F6F7FB]" style={{ top: (slotIdx + 1) * SLOT_PX }} />;
              })}
              {/* 15-min slot click / drop targets */}
              {Array.from({ length: HOUR_COUNT * SLOTS_PER_HOUR }, (_, si) => {
                const slotId = `${k}-${si}`;
                const isOver = dragOverKey === slotId;
                const slotDate = new Date(d);
                const totalMin = START_HOUR * 60 + si * SLOT_MINUTES;
                slotDate.setHours(Math.floor(totalMin / 60), totalMin % 60, 0, 0);
                return (
                  <button key={si}
                          onClick={() => onSlotClick(slotDate)}
                          onDragOver={(e) => { e.preventDefault(); setDragOverKey(slotId); }}
                          onDragLeave={() => setDragOverKey((cur) => cur === slotId ? null : cur)}
                          onDrop={(e) => onDropSlot(e, d, si)}
                          className={`absolute left-0 right-0 hover:bg-[#FAFAFE] ${isOver ? 'bg-[#EEF2FF]' : ''}`}
                          style={{ top: si * SLOT_PX, height: SLOT_PX }}
                          tabIndex={-1} />
                );
              })}
              {/* Events */}
              {dayEvents.map((e) => {
                const t = typeByKey[e.type_key] || { color: '#8E8FA5' };
                const isMine = !me || !e.created_by || e.created_by === me;
                const owner = !isMine ? ownersById[e.created_by] : null;
                const startDt = new Date(e.start_at);
                const endDt = new Date(e.end_at);
                const minutesFromStart = (startDt.getHours() - START_HOUR) * 60 + startDt.getMinutes();
                const durationMin = Math.max(15, (endDt - startDt) / 60000);
                const top = (minutesFromStart / 60) * HOUR_PX;
                const height = Math.max(18, (durationMin / 60) * HOUR_PX - 2);
                return (
                  <button key={e.id}
                          onClick={(ev) => { ev.stopPropagation(); onEventClick(e); }}
                          draggable={isMine}
                          onDragStart={(ev) => isMine && onDragStart(ev, e)}
                          data-testid={`cal-event-${e.id}`}
                          title={!isMine && owner ? `Agenda de ${owner.name}` : undefined}
                          className={`absolute left-1 right-1 text-left text-[10.5px] font-semibold rounded px-1.5 py-1 overflow-hidden ${isMine ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${e.status === 'done' ? 'line-through opacity-60' : ''}`}
                          style={{
                            top, height,
                            background: `${t.color}26`,
                            color: t.color,
                            border: `${!isMine ? '2px dashed' : '1px solid'} ${t.color}66`,
                            lineHeight: 1.15,
                          }}>
                    <div className="truncate flex items-center gap-1">
                      {owner && (
                        <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/70 text-[8px] font-bold flex-shrink-0">
                          {(owner.name || '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="truncate">{startDt.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})} · {e.title}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ start, days = 7, events, typeByKey, onSlotClick, onEventClick, onMoveEvent, settings, me, ownersById }) {
  const list = Array.from({ length: days }, (_, i) => addDays(start, i));
  return <TimeGrid days={list} events={events} typeByKey={typeByKey} onSlotClick={onSlotClick} onEventClick={onEventClick} onMoveEvent={onMoveEvent} settings={settings} me={me} ownersById={ownersById} />;
}
function DayGrid({ date, events, typeByKey, onSlotClick, onEventClick, onMoveEvent, settings, me, ownersById }) {
  const d0 = new Date(date); d0.setHours(0,0,0,0);
  return <TimeGrid days={[d0]} events={events} typeByKey={typeByKey} onSlotClick={onSlotClick} onEventClick={onEventClick} onMoveEvent={onMoveEvent} settings={settings} me={me} ownersById={ownersById} />;
}

/* =================== Agenda (list) =================== */
function AgendaList({ events, typeByKey, companies, prospects, onEventClick, me, ownersById = {} }) {
  if (events.length === 0) return (
    <div className="soft-card p-10 text-center text-[13px] text-[#8E8FA5]">Aucun événement prévu dans les 30 prochains jours.</div>
  );
  return (
    <div className="soft-card p-2 divide-y divide-[#F3F4F8]" data-testid="cal-agenda-list">
      {events.map((e) => {
        const t = typeByKey[e.type_key] || { color: '#8E8FA5', label: 'Événement' };
        const co = companies.find((c) => c.id === e.company_id);
        const pr = prospects.find((p) => p.id === e.prospect_id);
        const isMine = !me || !e.created_by || e.created_by === me;
        const owner = !isMine ? ownersById[e.created_by] : null;
        return (
          <button key={e.id} onClick={() => onEventClick(e)} data-testid={`cal-agenda-${e.id}`}
                  className={`w-full text-left px-3 py-3 hover:bg-[#FAFAFE] flex items-start gap-3 ${!isMine ? 'border-l-2 border-dashed border-[#C7C8D6]' : ''}`}>
            <div className="w-1 self-stretch rounded-full" style={{ background: t.color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: t.color }}>{t.label}</span>
                <span className={`text-[13px] font-semibold text-[#1A1B2E] truncate ${e.status === 'done' ? 'line-through opacity-60' : ''}`}>{e.title}</span>
                {owner && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#EEF2FF] text-[#4F46E5] inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-[#4F46E5] text-white text-[8px] flex items-center justify-center font-bold">
                      {(owner.name || '?').charAt(0).toUpperCase()}
                    </span>
                    {owner.name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-[11.5px] text-[#6B6C85] mt-0.5 flex-wrap">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(e.start_at).toLocaleString('fr-FR',{dateStyle:'short',timeStyle:'short'})}</span>
                {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</span>}
                {co && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {co.name}</span>}
                {pr && <span className="flex items-center gap-1">• {pr.company}</span>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* =================== Event Dialog (create / edit) =================== */
function EventDialog({ event, types, companies, prospects, settings, me, ownersById = {}, onClose, onSaved }) {
  const isNew = !!event.isNew;
  const isGoogle = event.source === 'google' || event.type_key === 'google';
  const isMine = !isGoogle && (isNew || !me || !event.created_by || event.created_by === me);
  const owner = !isMine && !isGoogle ? ownersById[event.created_by] : null;
  const slotMinutes = Math.max(5, Number(settings?.slot_minutes) || 15);
  const minDurationMs = slotMinutes * 60 * 1000;
  const timeOptions = useMemo(() => timeSlotOptions(slotMinutes), [slotMinutes]);
  const startInit = event.start_at ? new Date(event.start_at) : new Date();
  const endInit = event.end_at ? new Date(event.end_at) : new Date(startInit.getTime() + (settings?.default_duration_minutes || 30) * 60000);
  const [form, setForm] = useState({
    title: event.title || '',
    description: event.description || '',
    type_key: event.type_key || 'rdv',
    start_at: toIsoLocal(startInit),
    end_at: toIsoLocal(endInit),
    all_day: !!event.all_day,
    company_id: event.company_id || '',
    prospect_id: event.prospect_id || '',
    location: event.location || '',
    reminders: event.reminders || [15],
    status: event.status || 'scheduled',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  // --- Start/End split handlers -------------------------------------------
  // We split each datetime into a "date" and a "time" control so the native
  // browser picker can't show a minute-by-minute column. The time select
  // only exposes multiples of settings.slot_minutes. We still preserve the
  // previous duration when start changes, and clamp the end when it'd fall
  // before start + slot_minutes.
  const handleStartDateChange = (e) => {
    const newDate = e.target.value;
    if (!newDate) return;
    setForm((f) => {
      const prevStart = new Date(f.start_at);
      const prevEnd = new Date(f.end_at);
      let duration = prevEnd - prevStart;
      if (!isFinite(duration) || duration < minDurationMs) duration = minDurationMs;
      const { time } = splitLocal(f.start_at);
      const newStartIso = combineLocal(newDate, time);
      const newStart = new Date(newStartIso);
      return { ...f, start_at: newStartIso, end_at: toIsoLocal(new Date(newStart.getTime() + duration)) };
    });
  };
  const handleStartTimeChange = (e) => {
    const newTime = e.target.value;
    setForm((f) => {
      const prevStart = new Date(f.start_at);
      const prevEnd = new Date(f.end_at);
      let duration = prevEnd - prevStart;
      if (!isFinite(duration) || duration < minDurationMs) duration = minDurationMs;
      const { date } = splitLocal(f.start_at);
      const newStartIso = combineLocal(date, newTime);
      const newStart = new Date(newStartIso);
      return { ...f, start_at: newStartIso, end_at: toIsoLocal(new Date(newStart.getTime() + duration)) };
    });
  };
  const handleEndDateChange = (e) => {
    const newDate = e.target.value;
    if (!newDate) return;
    setForm((f) => {
      const { time } = splitLocal(f.end_at);
      const start = new Date(f.start_at);
      const end = new Date(combineLocal(newDate, time));
      if (end.getTime() < start.getTime() + minDurationMs) {
        return { ...f, end_at: toIsoLocal(new Date(start.getTime() + minDurationMs)) };
      }
      return { ...f, end_at: combineLocal(newDate, time) };
    });
  };
  const handleEndTimeChange = (e) => {
    const newTime = e.target.value;
    setForm((f) => {
      const { date } = splitLocal(f.end_at);
      const start = new Date(f.start_at);
      const end = new Date(combineLocal(date, newTime));
      if (end.getTime() < start.getTime() + minDurationMs) {
        return { ...f, end_at: toIsoLocal(new Date(start.getTime() + minDurationMs)) };
      }
      return { ...f, end_at: combineLocal(date, newTime) };
    });
  };

  const save = async () => {
    if (!form.title.trim()) { toast({ title: 'Titre requis' }); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description || null, type_key: form.type_key,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        all_day: form.all_day,
        company_id: form.company_id || null, prospect_id: form.prospect_id || null,
        location: form.location || null,
        reminders: form.reminders,
        status: form.status,
      };
      if (isNew) await api.post('/calendar/events', payload);
      else await api.put(`/calendar/events/${event.id}`, payload);
      toast({ title: isNew ? 'Événement créé' : 'Événement mis à jour' });
      onSaved();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    setDeleting(true);
    try { await api.delete(`/calendar/events/${event.id}`); toast({ title: 'Supprimé' }); onSaved(); }
    catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' }); }
    finally { setDeleting(false); }
  };
  const toggleDone = async () => {
    const next = form.status === 'done' ? 'scheduled' : 'done';
    try {
      await api.patch(`/calendar/events/${event.id}/status`, { status: next });
      setForm((f) => ({ ...f, status: next }));
      toast({ title: next === 'done' ? 'Marqué comme fait' : 'Remis en prévu' });
      onSaved();
    } catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' }); }
  };

  const reminderOptions = [0, 5, 15, 30, 60, 120, 1440];
  const toggleReminder = (mins) => {
    setForm((f) => {
      const s = new Set(f.reminders || []);
      if (s.has(mins)) s.delete(mins); else s.add(mins);
      return { ...f, reminders: Array.from(s).sort((a,b)=>a-b) };
    });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="cal-event-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Nouvel événement' : isGoogle ? 'Événement Google Agenda' : isMine ? 'Modifier l\'événement' : 'Aperçu de l\'événement'}</DialogTitle>
          <DialogDescription>
            {isGoogle
              ? 'Cet événement provient de votre Google Agenda. Modifiable uniquement côté Google.'
              : isMine
                ? 'Planifiez un RDV, une tâche ou une relance — liez-le à une entreprise ou une opportunité si besoin.'
                : `Événement de l'agenda partagé${owner ? ` de ${owner.name}` : ''}. Lecture seule.`}
          </DialogDescription>
        </DialogHeader>
        {isGoogle && (
          <div className="rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] px-3 py-2 text-[12px] text-[#065F46] flex items-center gap-2" data-testid="cal-google-banner">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="#0F9D58"><path d="M22 12.06C22 6.51 17.52 2 12 2S2 6.51 2 12.06 6.48 22.12 12 22.12s10-4.51 10-10.06z"/><path fill="#fff" d="M12 6.5l-1.5 4h-4l3.25 2.5L8.5 17 12 14.5 15.5 17l-1.25-4L17.5 10.5h-4z"/></svg>
            Importé depuis Google Agenda
            {event.google_html_link && (
              <a href={event.google_html_link} target="_blank" rel="noreferrer" className="ml-auto underline font-semibold">
                Ouvrir dans Google →
              </a>
            )}
          </div>
        )}
        {!isMine && !isGoogle && (
          <div className="rounded-lg border border-[#FED7AA] bg-[#FFF7ED] px-3 py-2 text-[12px] text-[#9A3412] flex items-center gap-2" data-testid="cal-readonly-banner">
            <Users className="w-3.5 h-3.5 flex-shrink-0" />
            Vous consultez l'agenda partagé{owner ? ` de ${owner.name}` : ''}. Seul le propriétaire peut modifier cet événement.
          </div>
        )}
        <fieldset disabled={!isMine} className={!isMine ? 'opacity-80' : ''}>
        <div className="space-y-3">
          <input value={form.title} onChange={set('title')} data-testid="cal-field-title"
                 placeholder="Titre de l'événement"
                 className="w-full h-11 rounded-xl border border-[#EFF0F6] px-3 text-[13px]" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-[#8E8FA5]">Type</label>
              <select value={form.type_key} onChange={set('type_key')} data-testid="cal-field-type"
                      className="w-full h-11 rounded-xl border border-[#EFF0F6] px-3 text-[13px] bg-white">
                {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#8E8FA5]">Statut</label>
              <select value={form.status} onChange={set('status')}
                      className="w-full h-11 rounded-xl border border-[#EFF0F6] px-3 text-[13px] bg-white">
                <option value="scheduled">Prévu</option>
                <option value="done">Fait</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-[#8E8FA5]">Début</label>
              <div className="flex gap-2">
                <input type="date" value={splitLocal(form.start_at).date} onChange={handleStartDateChange}
                       data-testid="cal-field-start-date"
                       className="flex-1 min-w-0 h-11 rounded-xl border border-[#EFF0F6] px-3 text-[13px]" />
                {!form.all_day && (
                  <select value={splitLocal(form.start_at).time} onChange={handleStartTimeChange}
                          data-testid="cal-field-start-time"
                          className="w-[90px] h-11 rounded-xl border border-[#EFF0F6] px-2 text-[13px] bg-white">
                    {/* Fallback option for legacy events whose time isn't aligned to slot_minutes */}
                    {!timeOptions.includes(splitLocal(form.start_at).time) && splitLocal(form.start_at).time && (
                      <option value={splitLocal(form.start_at).time}>{splitLocal(form.start_at).time}</option>
                    )}
                    {timeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#8E8FA5]">Fin</label>
              <div className="flex gap-2">
                <input type="date" value={splitLocal(form.end_at).date} onChange={handleEndDateChange}
                       data-testid="cal-field-end-date"
                       min={splitLocal(form.start_at).date}
                       className="flex-1 min-w-0 h-11 rounded-xl border border-[#EFF0F6] px-3 text-[13px]" />
                {!form.all_day && (
                  <select value={splitLocal(form.end_at).time} onChange={handleEndTimeChange}
                          data-testid="cal-field-end-time"
                          className="w-[90px] h-11 rounded-xl border border-[#EFF0F6] px-2 text-[13px] bg-white">
                    {!timeOptions.includes(splitLocal(form.end_at).time) && splitLocal(form.end_at).time && (
                      <option value={splitLocal(form.end_at).time}>{splitLocal(form.end_at).time}</option>
                    )}
                    {timeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
              </div>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-[12.5px] text-[#4A4B63] cursor-pointer">
            <input type="checkbox" checked={form.all_day} onChange={(e) => set('all_day')(e.target.checked)} />
            Toute la journée
          </label>
          <div>
            <label className="text-[11px] font-semibold text-[#8E8FA5] flex items-center gap-1"><MapPin className="w-3 h-3" /> Lieu (optionnel)</label>
            <input value={form.location} onChange={set('location')} placeholder="Adresse, lien visio…"
                   className="w-full h-11 rounded-xl border border-[#EFF0F6] px-3 text-[13px]" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#8E8FA5]">Description</label>
            <textarea value={form.description} onChange={set('description')} rows={3}
                      className="w-full rounded-xl border border-[#EFF0F6] px-3 py-2 text-[13px]" />
          </div>

          <div className="rounded-xl border border-[#EFF0F6] p-3 bg-[#FAFAFE]">
            <div className="text-[11.5px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2">Lier au CRM (optionnel)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-[#8E8FA5] flex items-center gap-1"><Building2 className="w-3 h-3" /> Entreprise</label>
                <select value={form.company_id} onChange={set('company_id')} data-testid="cal-field-company"
                        className="w-full h-10 rounded-lg border border-[#EFF0F6] px-2 text-[12.5px] bg-white">
                  <option value="">—</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-[#8E8FA5]">Opportunité</label>
                <select value={form.prospect_id} onChange={set('prospect_id')} data-testid="cal-field-prospect"
                        className="w-full h-10 rounded-lg border border-[#EFF0F6] px-2 text-[12.5px] bg-white">
                  <option value="">—</option>
                  {prospects.map((p) => <option key={p.id} value={p.id}>{p.company} ({p.stage})</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[11.5px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2 flex items-center gap-1"><Bell className="w-3 h-3" /> Rappels</div>
            <div className="flex flex-wrap gap-1.5">
              {reminderOptions.map((m) => {
                const active = (form.reminders || []).includes(m);
                const label = m === 0 ? 'Au moment' : m < 60 ? `${m} min avant` : m === 60 ? '1 h avant' : m < 1440 ? `${m/60} h avant` : '1 jour avant';
                return (
                  <button key={m} type="button" onClick={() => toggleReminder(m)} data-testid={`cal-reminder-${m}`}
                          className={`h-8 px-2.5 rounded-lg text-[11.5px] font-semibold border ${active ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white border-[#EFF0F6] text-[#6B6C85] hover:border-[#C7D2FE]'}`}>
                    {active && <Check className="w-3 h-3 inline mr-0.5" />}{label}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-[#8E8FA5] mt-1.5">Vous recevrez un toast dans l'app et un email.</div>
          </div>
        </div>
        </fieldset>
        <DialogFooter className="flex items-center justify-between">
          <div className="flex gap-2">
            {!isNew && isMine && (
              <>
                <button onClick={toggleDone} data-testid="cal-toggle-done"
                        className="h-10 px-3 rounded-xl border border-[#A7F3D0] text-[#15803D] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#ECFDF5]">
                  <CheckCircle2 className="w-4 h-4" />{form.status === 'done' ? 'Remettre en prévu' : 'Marquer comme fait'}
                </button>
                <button onClick={remove} disabled={deleting} data-testid="cal-delete"
                        className="h-10 px-3 rounded-xl border border-[#FCA5A5] text-[#B91C1C] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FEF2F2]">
                  <Trash2 className="w-4 h-4" />Supprimer
                </button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold">{isMine ? 'Annuler' : 'Fermer'}</button>
            {isMine && (
              <button onClick={save} disabled={saving} data-testid="cal-save"
                      className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Enregistrer
              </button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =================== Types dialog =================== */
function TypesDialog({ types, onClose, onChange }) {
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#F472B6');
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      await api.post('/calendar/event-types', { label: newLabel, color: newColor });
      setNewLabel(''); onChange?.();
    } catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' }); }
    finally { setSaving(false); }
  };
  const del = async (key) => {
    if (!window.confirm('Supprimer ce type ?')) return;
    try { await api.delete(`/calendar/event-types/${key}`); onChange?.(); }
    catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' }); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Types d'événements</DialogTitle>
          <DialogDescription>Chaque type a sa couleur. Les types intégrés ne peuvent pas être supprimés.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {types.map((t) => (
            <div key={t.key} className="flex items-center gap-2 p-2 rounded-lg border border-[#EFF0F6]">
              <span className="w-4 h-4 rounded-full" style={{ background: t.color }} />
              <span className="flex-1 text-[13px] font-semibold text-[#1A1B2E]">{t.label}</span>
              {t.builtin ? (
                <span className="text-[10.5px] font-bold bg-[#EEF2FF] text-[#4F46E5] px-1.5 py-0.5 rounded">Intégré</span>
              ) : (
                <button onClick={() => del(t.key)} className="h-7 px-2 rounded-md text-[#B91C1C] hover:bg-[#FEF2F2]" title="Supprimer">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="border-t border-[#EFF0F6] pt-3">
          <div className="text-[11.5px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2">Ajouter un type</div>
          <div className="flex gap-2">
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Nom du type"
                   className="flex-1 h-10 rounded-lg border border-[#EFF0F6] px-3 text-[12.5px]" />
            <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
                   className="w-10 h-10 rounded-lg border border-[#EFF0F6] cursor-pointer" />
            <button onClick={create} disabled={saving || !newLabel.trim()}
                    className="h-10 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold disabled:opacity-60">
              Ajouter
            </button>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold">Fermer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
